import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { DesignConfiguration } from '@/lib/api/design';
import { designAPI } from '@/lib/api/design';
import { defaultBrandingTokens } from '@/design-system/tokens/branding';
import * as Sentry from '@sentry/react';

/**
 * Convert hex color to HSL string (without hsl() wrapper)
 * Returns format: "H S% L%" for use with CSS hsl() function and opacity
 */
function hexToHSL(hex: string): string {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Parse hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Convert hex color to RGB comma-separated values
 * Returns format: "R, G, B" for use with CSS rgba() function
 * Example: rgba(var(--color-primary-button-rgb), 0.5)
 */
function hexToRGB(hex: string): string {
  // If it's a var() reference, return a default
  if (hex.startsWith('var(')) {
    return '255, 64, 129'; // Default accent color RGB
  }

  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Handle 3-character hex codes
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `${r}, ${g}, ${b}`;
}

interface DesignContextType {
  config: DesignConfiguration | null;
  isLoading: boolean;
  applyDesignToDOM: (config: DesignConfiguration) => void;
  designError: string | null;
}

const DesignContext = createContext<DesignContextType | undefined>(undefined);

export const useDesign = () => {
  const context = useContext(DesignContext);
  if (context === undefined) {
    throw new Error('useDesign must be used within a DesignProvider');
  }
  return context;
};

interface DesignProviderProps {
  children: ReactNode;
}

// Default design configuration
const defaultDesignConfig: DesignConfiguration = {
  enabled: true,
  theme_name: 'Purple Passion Premium',
  colors: {
    primary_gradient_start: '#2D1B69',
    primary_gradient_end: '#1A0F3D',
    secondary_gradient_start: '#3D2575',
    secondary_gradient_end: '#2D1B69',
    primary_button_gradient_start: '#2D1B69',
    primary_button_gradient_end: '#1A0F3D',
    primary_button_bg: '#E91E63',
    primary_button_hover: '#D81B60',
    secondary_button_bg: 'var(--color-secondary-button, #374151)',
    secondary_button_hover: 'var(--color-secondary-button-hover, #4b5563)',
    primary_text: 'var(--color-primary-text, #FFFFFF)',
    secondary_text: 'var(--color-secondary-text, #E0E0E0)',
    accent_text: 'var(--color-accent-text, #FF4081)',
    card_background: 'var(--color-card-background, rgba(255, 255, 255, 0.1))',
    border_color: 'var(--color-border, rgba(255, 255, 255, 0.2))'
  },
  typography: {
    primary_font: 'Poppins',
    secondary_font: 'Inter',
    accent_font: 'Montserrat',
    hero_title_size: 4,
    page_title_size: 2.5,
    section_title_size: 1.8,
    body_text_size: 1,
    small_text_size: 0.875,
    title_weight: 700,
    body_weight: 400
  },
  layout: {
    max_container_width: '1200px',
    content_padding: '2rem',
    section_spacing: '4rem',
    button_radius: '8px',
    card_radius: '12px',
    transition_duration: '0.3s',
    hover_scale: 1.05
  },
  branding: {
    hero_title: defaultBrandingTokens.heroTitle,
    hero_subtitle: defaultBrandingTokens.heroSubtitle,
    primary_cta_text: defaultBrandingTokens.primaryCTA,
    secondary_cta_text: defaultBrandingTokens.secondaryCTA,
    site_title: defaultBrandingTokens.siteTitle,
    meta_description: defaultBrandingTokens.metaDescription
  },
  character_visibility: {
    show_public_only_before_login: false
  }
};

export const DesignProvider = ({ children }: DesignProviderProps) => {
  const [appliedConfig, setAppliedConfig] = useState<DesignConfiguration | null>(null);
  const [designError, setDesignError] = useState<string | null>(null);

  const { data: config, isLoading } = useQuery<DesignConfiguration>({
    queryKey: ['design-configuration'],
    queryFn: (): Promise<DesignConfiguration> => designAPI.getDesignConfiguration()
      .then(res => {
        setDesignError(null);
        return res.data;
      })
      .catch((err: unknown) => {
        // Fail soft: Fehler merken, aber Default-Design verwenden
        const message = typeof err === 'object' && err && 'response' in err && (err as any).response?.data?.detail
          ? (err as any).response.data.detail
          : (typeof err === 'object' && err && 'message' in err ? String((err as any).message) : 'Design-Konfiguration konnte nicht geladen werden');
        setDesignError(message);
        if (typeof Sentry.captureMessage === 'function') {
          Sentry.captureMessage('design.error_load_configuration', {
            level: 'warning',
            extra: { message }
          } as any);
        }
        return defaultDesignConfig;
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3, // Retry failed requests
    retryDelay: 1000, // Wait 1 second between retries
  });

  // Stable applyDesignToDOM callback
  const applyDesignToDOM = useCallback((designConfig: DesignConfiguration) => {
    if (!designConfig.enabled) return;

    // Create or update CSS custom properties
    const root = document.documentElement;
    
    // Apply color palette (keine HSL-System-Overrides, nur design-spezifische Variablen)
    if (designConfig.colors) {
      root.style.setProperty('--color-primary-gradient-start', designConfig.colors.primary_gradient_start);
      root.style.setProperty('--color-primary-gradient-end', designConfig.colors.primary_gradient_end);
      root.style.setProperty('--color-secondary-gradient-start', designConfig.colors.secondary_gradient_start);
      root.style.setProperty('--color-secondary-gradient-end', designConfig.colors.secondary_gradient_end);
      // Dedicated primary button gradient colors
      root.style.setProperty('--color-primary-button-gradient-start', designConfig.colors.primary_button_gradient_start || designConfig.colors.primary_gradient_start);
      root.style.setProperty('--color-primary-button-gradient-end', designConfig.colors.primary_button_gradient_end || designConfig.colors.primary_gradient_end);
      root.style.setProperty('--color-gradient-accent-start', designConfig.colors.gradient_accent_start || '#B19CD9');
      root.style.setProperty('--color-primary-button', designConfig.colors.primary_button_bg);
      root.style.setProperty('--color-primary-button-hover', designConfig.colors.primary_button_hover);
      root.style.setProperty('--color-secondary-button', designConfig.colors.secondary_button_bg);
      root.style.setProperty('--color-secondary-button-hover', designConfig.colors.secondary_button_hover);
      root.style.setProperty('--color-primary-text', designConfig.colors.primary_text);
      root.style.setProperty('--color-secondary-text', designConfig.colors.secondary_text);
      root.style.setProperty('--color-accent-text', designConfig.colors.accent_text);
      root.style.setProperty('--color-card-background', designConfig.colors.card_background);
      root.style.setProperty('--color-border', designConfig.colors.border_color);
      
      // Glass effect colors - use CSS variable fallbacks defined in design-variables.css
      // No need to set hardcoded values here, CSS handles the fallbacks
      
      // Post action colors (optional overrides - defaults in CSS)
      if (designConfig.colors.action_like) {
        root.style.setProperty('--color-action-like', designConfig.colors.action_like);
        root.style.setProperty('--color-action-like-hover', designConfig.colors.action_like);
      }
      if (designConfig.colors.action_like_active) {
        root.style.setProperty('--color-action-like-active', designConfig.colors.action_like_active);
      }
      if (designConfig.colors.action_comment) {
        root.style.setProperty('--color-action-comment', designConfig.colors.action_comment);
        root.style.setProperty('--color-action-comment-hover', designConfig.colors.action_comment);
      }
      if (designConfig.colors.action_view) {
        root.style.setProperty('--color-action-view', designConfig.colors.action_view);
        root.style.setProperty('--color-action-view-hover', designConfig.colors.action_view);
      }

      // Brand colors as HSL values (for use with opacity modifiers in CSS)
      // Format: "H S% L%" for use like: hsl(var(--brand-primary) / 0.5)
      root.style.setProperty('--brand-primary', hexToHSL(designConfig.colors.primary_button_bg));
      root.style.setProperty('--brand-primary-hex', designConfig.colors.primary_button_bg);
      root.style.setProperty('--brand-secondary', hexToHSL(designConfig.colors.primary_gradient_start));
      root.style.setProperty('--brand-secondary-hex', designConfig.colors.primary_gradient_start);

      // RGB color values for use with rgba()
      // Format: "R, G, B" for use like: rgba(var(--color-primary-button-rgb), 0.5)
      root.style.setProperty('--color-primary-gradient-start-rgb', hexToRGB(designConfig.colors.primary_gradient_start));
      root.style.setProperty('--color-primary-gradient-end-rgb', hexToRGB(designConfig.colors.primary_gradient_end));
      root.style.setProperty('--color-primary-button-rgb', hexToRGB(designConfig.colors.primary_button_bg));
      root.style.setProperty('--color-accent-text-rgb', hexToRGB(designConfig.colors.accent_text));
      root.style.setProperty('--brand-primary-rgb', hexToRGB(designConfig.colors.primary_button_bg));
      root.style.setProperty('--brand-secondary-rgb', hexToRGB(designConfig.colors.primary_gradient_start));

      // Status colors (optional overrides)
      const colors = designConfig.colors as any;
      if (colors.success) {
        root.style.setProperty('--color-success', colors.success);
      }
      if (colors.warning) {
        root.style.setProperty('--color-warning', colors.warning);
      }
      if (colors.error) {
        root.style.setProperty('--color-error', colors.error);
      }
      if (colors.info) {
        root.style.setProperty('--color-info', colors.info);
      }

      // Chart colors (optional overrides)
      if (colors.chart_1) root.style.setProperty('--color-chart-1', colors.chart_1);
      if (colors.chart_2) root.style.setProperty('--color-chart-2', colors.chart_2);
      if (colors.chart_3) root.style.setProperty('--color-chart-3', colors.chart_3);
      if (colors.chart_4) root.style.setProperty('--color-chart-4', colors.chart_4);
      if (colors.chart_5) root.style.setProperty('--color-chart-5', colors.chart_5);
      if (colors.chart_6) root.style.setProperty('--color-chart-6', colors.chart_6);
      if (colors.chart_7) root.style.setProperty('--color-chart-7', colors.chart_7);
      if (colors.chart_8) root.style.setProperty('--color-chart-8', colors.chart_8);

      // Rarity colors (optional overrides)
      if (colors.rarity_common) root.style.setProperty('--color-rarity-common', colors.rarity_common);
      if (colors.rarity_uncommon) root.style.setProperty('--color-rarity-uncommon', colors.rarity_uncommon);
      if (colors.rarity_rare) root.style.setProperty('--color-rarity-rare', colors.rarity_rare);
      if (colors.rarity_epic) root.style.setProperty('--color-rarity-epic', colors.rarity_epic);
      if (colors.rarity_legendary) root.style.setProperty('--color-rarity-legendary', colors.rarity_legendary);

      // Swipe colors (optional overrides)
      if (colors.swipe_like) root.style.setProperty('--color-swipe-like', colors.swipe_like);
      if (colors.swipe_superlike) root.style.setProperty('--color-swipe-superlike', colors.swipe_superlike);
      if (colors.swipe_dislike) root.style.setProperty('--color-swipe-dislike', colors.swipe_dislike);

      // Navigation colors (optional overrides)
      if (colors.nav_icon_active) root.style.setProperty('--color-nav-icon-active', colors.nav_icon_active);
      if (colors.nav_badge_bg) root.style.setProperty('--color-nav-badge-bg', colors.nav_badge_bg);

      // Glass dropdown panel colors (for menus, notifications, overlays)
      if (colors.glass_dropdown_bg) root.style.setProperty('--glass-dropdown-bg', colors.glass_dropdown_bg);
      if (colors.glass_dropdown_border) root.style.setProperty('--glass-dropdown-border', colors.glass_dropdown_border);
      if (colors.glass_dropdown_highlight) root.style.setProperty('--glass-dropdown-highlight', colors.glass_dropdown_highlight);
    }

    // Apply typography
    if (designConfig.typography) {
      root.style.setProperty('--font-primary', designConfig.typography.primary_font);
      root.style.setProperty('--font-secondary', designConfig.typography.secondary_font);
      root.style.setProperty('--font-accent', designConfig.typography.accent_font);
      root.style.setProperty('--font-size-hero', `${designConfig.typography.hero_title_size}rem`);
      root.style.setProperty('--font-size-page-title', `${designConfig.typography.page_title_size}rem`);
      root.style.setProperty('--font-size-section-title', `${designConfig.typography.section_title_size}rem`);
      root.style.setProperty('--font-size-body', `${designConfig.typography.body_text_size}rem`);
      root.style.setProperty('--font-size-small', `${designConfig.typography.small_text_size}rem`);
      root.style.setProperty('--font-weight-title', designConfig.typography.title_weight.toString());
      root.style.setProperty('--font-weight-body', designConfig.typography.body_weight.toString());
    }

    // Apply layout settings
    if (designConfig.layout) {
      root.style.setProperty('--layout-max-width', designConfig.layout.max_container_width);
      root.style.setProperty('--layout-padding', designConfig.layout.content_padding);
      root.style.setProperty('--layout-section-spacing', designConfig.layout.section_spacing);
      root.style.setProperty('--border-radius-button', designConfig.layout.button_radius);
      root.style.setProperty('--border-radius-card', designConfig.layout.card_radius);
      // globaler Radius für viele Komponenten
      root.style.setProperty('--radius', designConfig.layout.card_radius || '12px');
      root.style.setProperty('--transition-duration', designConfig.layout.transition_duration);
      root.style.setProperty('--hover-scale', designConfig.layout.hover_scale.toString());

      // Extended spacing tokens
      const spacing = designConfig.layout.spacing;
      if (spacing) {
        if (spacing.xs) root.style.setProperty('--spacing-xs', spacing.xs);
        if (spacing.sm) root.style.setProperty('--spacing-sm', spacing.sm);
        if (spacing.md) root.style.setProperty('--spacing-md', spacing.md);
        if (spacing.lg) root.style.setProperty('--spacing-lg', spacing.lg);
        if (spacing.xl) root.style.setProperty('--spacing-xl', spacing.xl);
        if (spacing.xxl) root.style.setProperty('--spacing-xxl', spacing.xxl);
        if (spacing.xxxl) root.style.setProperty('--spacing-xxxl', spacing.xxxl);
      }

      // Extended radius tokens
      const radius = designConfig.layout.radius;
      if (radius) {
        if (radius.none) root.style.setProperty('--radius-none', radius.none);
        if (radius.sm) root.style.setProperty('--radius-sm', radius.sm);
        if (radius.md) root.style.setProperty('--radius-md', radius.md);
        if (radius.lg) root.style.setProperty('--radius-lg', radius.lg);
        if (radius.xl) root.style.setProperty('--radius-xl', radius.xl);
        if (radius.xxl) root.style.setProperty('--radius-xxl', radius.xxl);
        if (radius.full) root.style.setProperty('--radius-full', radius.full);
        // Semantic radius aliases
        if (radius.button) root.style.setProperty('--radius-button', radius.button);
        if (radius.card) root.style.setProperty('--radius-card', radius.card);
        if (radius.input) root.style.setProperty('--radius-input', radius.input);
        if (radius.modal) root.style.setProperty('--radius-modal', radius.modal);
        if (radius.avatar) root.style.setProperty('--radius-avatar', radius.avatar);
      }

      // Extended shadow tokens
      const shadows = designConfig.layout.shadows;
      if (shadows) {
        if (shadows.sm) root.style.setProperty('--shadow-sm', shadows.sm);
        if (shadows.md) root.style.setProperty('--shadow-md', shadows.md);
        if (shadows.lg) root.style.setProperty('--shadow-lg', shadows.lg);
        if (shadows.xl) root.style.setProperty('--shadow-xl', shadows.xl);
        // Semantic shadow aliases
        if (shadows.card) root.style.setProperty('--shadow-card', shadows.card);
        if (shadows.dropdown) root.style.setProperty('--shadow-dropdown', shadows.dropdown);
        if (shadows.modal) root.style.setProperty('--shadow-modal', shadows.modal);
        if (shadows.glow) root.style.setProperty('--shadow-glow', shadows.glow);
        if (shadows.glow_primary) root.style.setProperty('--shadow-glow-primary', shadows.glow_primary);
        if (shadows.inner) root.style.setProperty('--shadow-inner', shadows.inner);
      }

      // Extended animation tokens
      const animation = designConfig.layout.animation;
      if (animation) {
        if (animation.duration_fast) root.style.setProperty('--animation-duration-fast', animation.duration_fast);
        if (animation.duration_normal) root.style.setProperty('--animation-duration-normal', animation.duration_normal);
        if (animation.duration_slow) root.style.setProperty('--animation-duration-slow', animation.duration_slow);
        if (animation.easing_default) root.style.setProperty('--animation-easing-default', animation.easing_default);
        if (animation.easing_in) root.style.setProperty('--animation-easing-in', animation.easing_in);
        if (animation.easing_out) root.style.setProperty('--animation-easing-out', animation.easing_out);
        if (animation.hover_scale) root.style.setProperty('--animation-hover-scale', animation.hover_scale.toString());
        if (animation.press_scale) root.style.setProperty('--animation-press-scale', animation.press_scale.toString());
      }

      // Extended component tokens
      const components = designConfig.layout.components;
      if (components) {
        // Button tokens
        if (components.button_height_sm) root.style.setProperty('--component-button-height-sm', components.button_height_sm);
        if (components.button_height_md) root.style.setProperty('--component-button-height-md', components.button_height_md);
        if (components.button_height_lg) root.style.setProperty('--component-button-height-lg', components.button_height_lg);
        if (components.button_padding_x) root.style.setProperty('--component-button-padding-x', components.button_padding_x);
        if (components.button_font_weight) root.style.setProperty('--component-button-font-weight', components.button_font_weight.toString());
        // Input tokens
        if (components.input_height) root.style.setProperty('--component-input-height', components.input_height);
        if (components.input_padding_x) root.style.setProperty('--component-input-padding-x', components.input_padding_x);
        if (components.input_border_width) root.style.setProperty('--component-input-border-width', components.input_border_width);
        // Card tokens
        if (components.card_padding) root.style.setProperty('--component-card-padding', components.card_padding);
        if (components.card_gap) root.style.setProperty('--component-card-gap', components.card_gap);
        // Navigation tokens
        if (components.nav_height_desktop) root.style.setProperty('--component-nav-height-desktop', components.nav_height_desktop);
        if (components.nav_height_mobile) root.style.setProperty('--component-nav-height-mobile', components.nav_height_mobile);
      }
    }

    // Apply custom CSS if provided
    if (designConfig.custom_css) {
      const existingCustomStyle = document.getElementById('custom-design-css');
      if (existingCustomStyle) {
        existingCustomStyle.remove();
      }

      const customStyle = document.createElement('style');
      customStyle.id = 'custom-design-css';
      customStyle.textContent = designConfig.custom_css;
      document.head.appendChild(customStyle);
    }

    // Update document title if branding is provided
    if (designConfig.branding?.site_title) {
      document.title = designConfig.branding.site_title;
    }

    // Update meta description
    if (designConfig.branding?.meta_description) {
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', designConfig.branding.meta_description);
      } else {
        const newMetaDescription = document.createElement('meta');
        newMetaDescription.name = 'description';
        newMetaDescription.content = designConfig.branding.meta_description;
        document.head.appendChild(newMetaDescription);
      }
    }

    // Update favicon dynamically if provided
    try {
      const head = document.head;
      // Remove existing favicons
      Array.from(head.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]')).forEach(el => el.parentElement?.removeChild(el));
      const faviconUrl = designConfig.branding?.favicon_url;
      if (faviconUrl) {
        const link = document.createElement('link');
        link.rel = 'icon';
        link.href = faviconUrl;
        head.appendChild(link);
        const link2 = document.createElement('link');
        link2.rel = 'shortcut icon';
        link2.href = faviconUrl;
        head.appendChild(link2);
        // Expose to window for Notification API usage
        (window as any).__APP_FAVICON__ = faviconUrl;
      }
    } catch (_) {
      // noop
    }

    // Update meta robots tag for SEO
    try {
      const robotsIndex = designConfig.branding?.robots_index !== false ? 'index' : 'noindex';
      const robotsFollow = designConfig.branding?.robots_follow !== false ? 'follow' : 'nofollow';
      const robotsContent = `${robotsIndex}, ${robotsFollow}`;

      let metaRobots = document.querySelector('meta[name="robots"]');
      if (metaRobots) {
        metaRobots.setAttribute('content', robotsContent);
      } else {
        metaRobots = document.createElement('meta');
        metaRobots.setAttribute('name', 'robots');
        metaRobots.setAttribute('content', robotsContent);
        document.head.appendChild(metaRobots);
      }
    } catch (_) {
      // noop
    }

    setAppliedConfig(designConfig);
  }, []);

  // Apply design configuration when it loads
  useEffect(() => {
    const configToApply = config || defaultDesignConfig;
    if (configToApply.enabled) {
      applyDesignToDOM(configToApply);
    }
  }, [config]);

  // Preview listener: erlaubt temporäres Anwenden eines Presets innerhalb einer Vorschau (z. B. iFrame "?preview=1")
  useEffect(() => {
    try {
      const search = typeof window !== 'undefined' ? window.location.search : '';
      const isPreview = new URLSearchParams(search).get('preview') === '1';
      if (!isPreview) return;

      const handler = (ev: MessageEvent) => {
        // nur gleiche Origin akzeptieren
        if (typeof window !== 'undefined' && ev.origin !== window.location.origin) return;
        const data = ev.data as unknown;
        if (!data || typeof data !== 'object') return;
        const maybe = data as { type?: string; payload?: DesignConfiguration };
        if (maybe.type === 'design-preview-apply' && maybe.payload) {
          applyDesignToDOM(maybe.payload);
        }
      };

      window.addEventListener('message', handler);
      return () => window.removeEventListener('message', handler);
    } catch (_) {
      // noop
    }
  }, [applyDesignToDOM]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<DesignContextType>(() => ({
    config: appliedConfig || config || defaultDesignConfig,
    isLoading,
    applyDesignToDOM,
    designError,
  }), [appliedConfig, config, isLoading, applyDesignToDOM, designError]);

  return (
    <DesignContext.Provider value={value}>
      {children}
    </DesignContext.Provider>
  );
};